/** Reading a time of day the way somebody types one: `930`, `9.30`, `9h30` or a bare `9`. */

/** `HH:mm` for anything that reads as a time of day, or undefined when it does not read as one. */
export const parseClockTime = (raw: string): string | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  // Any of the separators people reach for, or none at all: 9:30, 9.30, 9h30, 930.
  const match = /^(\d{1,2})(?:[:.,hH]?(\d{2}))?$/.exec(trimmed);
  if (!match) return undefined;

  const hours = Number(match[1]);
  const minutes = match[2] === undefined ? 0 : Number(match[2]);
  if (hours > 23 || minutes > 59) return undefined;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/** The latest whole time two half-typed boxes could still become, so a caller refuses no keystroke too soon. */
export const latestClockTime = (hour: string, minute: string): string | undefined => {
  if (hour === '' || Number(hour) > 23) return undefined;
  const hourCanGrow = hour.length < 2 && Number(hour) <= 2;
  const hours = Math.min(hourCanGrow ? Number(`${hour}9`) : Number(hour), 23);
  // An empty box is every minute of the hour, not the ninth: `${''}9` reads as 9, the wrong end.
  const minutes = minute.length >= 2 ? Number(minute) : Number(`${minute === '' ? '5' : minute}9`);
  const pad = (part: number): string => String(part).padStart(2, '0');
  return `${pad(hours)}:${pad(Math.min(minutes, 59))}`;
};

/** Minutes into the day, for a value that has already been read as a time. */
export const clockMinutes = (raw: string): number | undefined => {
  const time = parseClockTime(raw);
  if (!time) return undefined;
  return Number(time.slice(0, 2)) * 60 + Number(time.slice(3));
};
